/**
 * Does a talent's skill cover a demand's skill? The one rule for it.
 *
 * Every place that scores a talent against a demand uses this: the opportunity
 * cards' "N of M skills" and "Skills you bring", the dashboard's suggestions,
 * the on-page closing-soon reminders. The server's closing-soon emails apply
 * the same rule, so a card and an email cannot disagree.
 *
 * A talent's term T covers a demand's term D when:
 *  - they are the same term, case and spacing aside;
 *  - T is D's category — a generalist covers every skill in it;
 *  - D is a category and T is one of its skills — a demand that asks for the
 *    whole category is served by any skill in it.
 *
 * Two different skills in the same category do NOT match. Terms outside every
 * category (an older catalog entry, a word grandfathered on a demand) match
 * only themselves, as before categories existed.
 */

import type { Capability } from '../types/capability'

/** Case- and whitespace-insensitive identity — the server's `_key()`. */
export function skillKey(term: string): string {
  return term.trim().toLowerCase().split(/\s+/).join(' ')
}

/** Which category each catalog skill sits in, both as `skillKey`s. */
export interface SkillIndex {
  readonly categoryOf: ReadonlyMap<string, string>
}

/** No catalog yet, or one without categories: terms match only themselves. */
export const NO_CATEGORIES: SkillIndex = { categoryOf: new Map() }

export function skillIndex(catalog: Capability[] | null | undefined): SkillIndex {
  // Anything but a list reads as no catalog: a failed or still-loading read
  // degrades to plain name matching rather than taking the page down.
  const rows = Array.isArray(catalog) ? catalog : []
  const nameById = new Map(rows.map((c) => [c.id, c.name] as const))
  const categoryOf = new Map<string, string>()
  for (const c of rows) {
    if (c.is_category) continue
    const parent = c.category ?? (c.parent_id != null ? nameById.get(c.parent_id) : undefined)
    if (parent) categoryOf.set(skillKey(c.name), skillKey(parent))
  }
  return { categoryOf }
}

export function covers(index: SkillIndex, talentTerm: string, demandTerm: string): boolean {
  const t = skillKey(talentTerm)
  const d = skillKey(demandTerm)
  if (!t || !d) return false
  return t === d || index.categoryOf.get(d) === t || index.categoryOf.get(t) === d
}

/** The demand's terms the talent covers, in the demand's order and spelling. */
export function coveredSkills(
  index: SkillIndex,
  talentTerms: string[],
  demandTerms: string[],
): string[] {
  return demandTerms.filter((d) => talentTerms.some((t) => covers(index, t, d)))
}
